import type React from 'react';
import { useMemo, useState } from 'react';
import { Alert, Box, Chip, TextField, Typography } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Clapperboard, Search } from 'lucide-react';
import { EmptyState } from '../../../../components/admin/EmptyState';
import { formatAdminNumber } from '../../../../utils/numberFormat';
import { useAdminVideoJobs, useGenerateVideoHealth } from '../api';
import { JobStatusChip } from '../components/JobStatusChip';
import { VideoHealthAlerts } from '../components/VideoHealthAlerts';
import { VideoJobDetailDrawer } from '../components/VideoJobDetailDrawer';
import { VideoServiceOverview } from '../components/VideoServiceOverview';
import { useDebouncedValue } from '../hooks';
import type { AdminVideoJobRow, VideoJobStatus } from '../types';

const STATUSES: VideoJobStatus[] = ['queued', 'running', 'completed', 'failed', 'cancelled'];
const PAGE_SIZE_OPTIONS = [20, 50, 100];
const SEARCH_DEBOUNCE_MS = 400;

const columns: GridColDef<AdminVideoJobRow>[] = [
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    renderCell: (params) => <JobStatusChip status={params.row.status} />,
  },
  { field: 'capabilityCode', headerName: 'Operation', width: 140 },
  {
    field: 'userEmail',
    headerName: 'User',
    flex: 1,
    minWidth: 200,
    valueGetter: (_value, row) => row.userEmail ?? `#${row.userId}`,
  },
  {
    field: 'modelCode',
    headerName: 'Model',
    flex: 1,
    minWidth: 200,
    valueGetter: (_value, row) => `${row.costTier ?? ''} · ${row.modelCode ?? ''}`,
  },
  {
    field: 'chargedTokens',
    headerName: 'Credits (est. / charged)',
    width: 190,
    valueGetter: (_value, row) =>
      `${formatAdminNumber(row.estimatedTokens ?? 0)} / ${formatAdminNumber(row.chargedTokens ?? 0)}`,
  },
  { field: 'errorCode', headerName: 'Error', width: 180 },
  {
    field: 'createdAt',
    headerName: 'Created',
    width: 180,
    valueGetter: (_value, row) => new Date(row.createdAt).toLocaleString(),
  },
];

/** Generate Video monitoring: configuration health, then all jobs with filters (spec §9). */
export const GenerateVideoJobsPage: React.FC = () => {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE_OPTIONS[0],
  });
  const [status, setStatus] = useState<VideoJobStatus | undefined>();
  const [capabilityCode, setCapabilityCode] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);

  const { data: health } = useGenerateVideoHealth();
  const filters = useMemo(
    () => ({
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      status,
      capabilityCode,
      search: debouncedSearch || undefined,
    }),
    [capabilityCode, debouncedSearch, paginationModel, status]
  );
  const { data, isLoading, isFetching, error } = useAdminVideoJobs(filters);
  const resetPage = () => setPaginationModel((model) => ({ ...model, page: 0 }));

  return (
    <Box className="min-h-screen space-y-6 bg-slate-50 p-4 dark:bg-slate-900 md:p-6">
      <Box>
        <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
          Generate Video
        </Typography>
        <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
          Models and prices users can pick, and every video job with its usage.
        </Typography>
      </Box>

      <VideoHealthAlerts health={health} />
      <VideoServiceOverview health={health} />

      <Box className="rounded-xl bg-white shadow-sm dark:bg-slate-800">
        <Box className="space-y-3 border-b border-gray-200 p-4 dark:border-slate-700">
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              resetPage();
            }}
            placeholder="Search by job ID, request ID, model or user email"
            InputProps={{ startAdornment: <Search size={16} className="mr-2 text-gray-400" /> }}
          />
          <Box className="flex flex-wrap gap-2">
            <Chip
              label="All statuses"
              color={!status ? 'primary' : 'default'}
              variant={!status ? 'filled' : 'outlined'}
              onClick={() => {
                setStatus(undefined);
                resetPage();
              }}
            />
            {STATUSES.map((value) => (
              <Chip
                key={value}
                label={value}
                color={status === value ? 'primary' : 'default'}
                variant={status === value ? 'filled' : 'outlined'}
                onClick={() => {
                  setStatus(value);
                  resetPage();
                }}
              />
            ))}
            <Box className="mx-1 w-px self-stretch bg-gray-200 dark:bg-slate-700" />
            <Chip
              label="All operations"
              color={!capabilityCode ? 'primary' : 'default'}
              variant={!capabilityCode ? 'filled' : 'outlined'}
              onClick={() => {
                setCapabilityCode(undefined);
                resetPage();
              }}
            />
            {(health?.operations ?? []).map((operation) => (
              <Chip
                key={operation.capabilityCode}
                label={operation.capabilityCode}
                color={capabilityCode === operation.capabilityCode ? 'primary' : 'default'}
                variant={capabilityCode === operation.capabilityCode ? 'filled' : 'outlined'}
                onClick={() => {
                  setCapabilityCode(operation.capabilityCode);
                  resetPage();
                }}
              />
            ))}
          </Box>
        </Box>

        {error ? (
          <Box className="p-4">
            <Alert severity="error">Failed to load video jobs.</Alert>
          </Box>
        ) : !isLoading && (data?.data.length ?? 0) === 0 ? (
          <Box className="p-10">
            <EmptyState
              Icon={Clapperboard}
              title="No video jobs"
              description="Jobs appear here once users generate videos."
            />
          </Box>
        ) : (
          <DataGrid
            rows={data?.data ?? []}
            columns={columns}
            getRowId={(row) => row.jobId}
            loading={isLoading || isFetching}
            rowCount={data?.pagination.total ?? 0}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onRowClick={(params) => setSelectedJobId(params.row.jobId)}
            disableRowSelectionOnClick
            autoHeight
            className="border-0"
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        )}
      </Box>

      <VideoJobDetailDrawer jobId={selectedJobId} onClose={() => setSelectedJobId(null)} />
    </Box>
  );
};
